// Shared project profitability maths — used by the project form's sticky
// ProfitabilityBar and the admin Projects table.
//
// Fee basis per scope: Contract Value when set, otherwise the scope's
// scheduled monthly total. Sub-consultant rows are a pass-through cost and are
// SUBTRACTED, so the fee basis is the NET fee the firm keeps (firm model,
// 2026-06). Invoicing still works from the gross total elsewhere.
// Personnel cost: sum over merged allocations (named + TBD tier rows) of
// (row FTE × tier-average monthly rate). Legacy `tierResourcing` rows are
// folded in via `mergedAllocations` so untouched projects still compute
// correctly during the transition to the single merged grid.
import {
  DRIVER_RATE_PER_PERSON_MONTH,
  TARGET_MARGIN_THRESHOLD,
} from "./constants";
import { mergedAllocations } from "./allocations";
import { subMagnitude } from "./subconsultants";

export const scopeFee = (s) => {
  const cv = Number(s.contractValue) || 0;
  if (cv > 0) return cv;
  return Object.values(s.revenue || {}).reduce((a, b) => a + (b || 0), 0);
};

export const rateForTier = (rates, tier) =>
  rates?.tiers?.[tier]?.count ? rates.tiers[tier].avgMonthly : DRIVER_RATE_PER_PERSON_MONTH;

// Resolve an allocation row's tier — named rows may carry it explicitly OR
// look it up from `people`. TBD rows always carry `tier`.
const rowTier = (row, people) => {
  if (row.tier) return row.tier;
  if (row.tbd) return null;
  return (people || []).find((p) => p.id === row.personId)?.tier || null;
};

export function projectProfitability(project, rates, people) {
  const scopes = project?.scopes || [];
  // Fee basis is the NET fee: gross stages minus sub-consultant pass-through
  // cost (sub rows are stored negative; magnitude taken explicitly so the sign
  // convention can't double-count).
  const firm = scopes.filter((s) => !s.isSubConsultant);
  const subs = scopes
    .filter((s) => s.isSubConsultant)
    .reduce((sum, s) => sum + subMagnitude(s), 0);
  const grossFee = firm.reduce((sum, s) => sum + scopeFee(s), 0);
  const fee = grossFee - subs;
  const weightedFee = firm.reduce((sum, s) => sum + scopeFee(s) * (s.probability ?? 1), 0) - subs;

  const rows = mergedAllocations(project);
  let personMonths = 0, cost = 0;
  if (rows.length) {
    for (const r of rows) {
      // Rejected proposals never count. Removal-requested rows still count
      // until management approves the removal (matches capacity behaviour).
      if (r.status === "rejected") continue;
      const q = Object.values(r.load || {}).reduce((a, b) => a + (b || 0), 0);
      personMonths += q;
      const tier = rowTier(r, people);
      cost += q * (tier ? rateForTier(rates, tier) : DRIVER_RATE_PER_PERSON_MONTH);
    }
  } else {
    personMonths = Object.values(project?.peopleNeededPerMonth || {}).reduce((a, b) => a + (b || 0), 0);
    cost = personMonths * DRIVER_RATE_PER_PERSON_MONTH;
  }

  const margin = fee - cost;
  const marginPct = fee > 0 ? margin / fee : null;
  const wMargin = weightedFee - cost;
  const wMarginPct = weightedFee > 0 ? wMargin / weightedFee : null;
  const revPerPm = personMonths > 0 ? fee / personMonths : null;
  return { fee, grossFee, subs, cost, margin, marginPct, weightedFee, wMargin, wMarginPct, revPerPm, personMonths };
}

// Colour for a margin percentage — green ≥ threshold, amber 0–threshold, red < 0.
export const MARGIN_GREEN = "#2C4A38";
export const MARGIN_AMBER = "#8a6d1a";
export const MARGIN_RED = "#6B2F2A";

export const marginColor = (marginPct) => {
  if (marginPct == null) return "var(--ink)";
  if (marginPct < 0) return MARGIN_RED;
  if (marginPct < TARGET_MARGIN_THRESHOLD) return MARGIN_AMBER;
  return MARGIN_GREEN;
};
